<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <span class="admin-kicker">Standorte</span>
          <h1 class="admin-page-title">Schichtdokumente &amp; Rundgänge</h1>
          <p class="admin-page-subtitle">
            Dokumente, Kundenzugriffe und Rundgänge zentral verwalten.
          </p>
        </div>
      </div>
      <div
        v-if="errorMessage"
        class="settings-message settings-message--error"
        role="alert"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="successMessage"
        class="settings-message settings-message--success"
        role="status"
      >
        {{ successMessage }}
      </div>
      <div v-if="canManageExternalAccess" class="admin-panel open-docs">
        <div class="admin-panel__header open-docs__header">
          <h2 class="admin-panel__title">Offene Dokumente</h2>
          <span class="open-docs__count">{{ openDocuments.length }}</span>
        </div>
        <div class="admin-panel__body open-docs__body">
          <div class="table-scroll">
            <table class="settings-table">
              <thead>
                <tr>
                  <th>Vertrag</th>
                  <th>Standort</th>
                  <th>Typ</th>
                  <th>Datum</th>
                  <th>Verfasser</th>
                  <th class="text-center">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="openDocumentsLoading">
                  <td colspan="6" class="settings-table__empty">
                    <span class="open-docs__loading" role="status">
                      <span class="open-docs__spinner" aria-hidden="true"></span>
                      Offene Dokumente werden geladen …
                    </span>
                  </td>
                </tr>
                <tr v-for="document in openDocuments" :key="document.id">
                  <td>
                    {{
                      document.jobdata
                        ? document.jobdata.Vertragsbeschreibung
                        : "–"
                    }}
                  </td>
                  <td>
                    {{
                      document.jobdata
                        ? document.jobdata.Einsatzort_Beschreibung
                        : "–"
                    }}
                  </td>
                  <td>{{ document.type }}</td>
                  <td>
                    {{
                      document.document && document.document.date
                        ? document.document.date
                        : "–"
                    }}
                  </td>
                  <td>
                    {{
                      document.document &&
                      document.document.author &&
                      document.document.author.name
                        ? document.document.author.name
                        : "–"
                    }}
                  </td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="admin-button--secondary admin-button--sm"
                      @click="viewDocument(document)"
                    >
                      Anzeigen
                    </button>
                  </td>
                </tr>
                <tr v-if="!openDocumentsLoading && openDocuments.length === 0">
                  <td colspan="6" class="settings-table__empty">
                    Keine offenen Dokumente vorhanden.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="admin-panel selection-panel">
        <div class="admin-panel__header selection-panel__header">
          <div>
            <h2 class="admin-panel__title">Standort auswählen</h2>
            <p class="selection-panel__hint">
              Vertrag → Standort → Einsatzstelle
            </p>
          </div>
          <button
            type="button"
            class="admin-button--ghost admin-button--sm"
            @click="reset"
          >
            Zurücksetzen
          </button>
        </div>
        <div class="admin-panel__body">
          <div v-if="presetsLoading" class="selection-panel__loading">
            Standorte werden geladen …
          </div>
          <div v-else class="selection-grid">
            <AdminSearchSelect
              input-id="contract-select"
              label="Vertrag"
              :options="contracts"
              :model-value="selectedContract"
              placeholder="Vertrag suchen"
              status-mode="features"
              @update:model-value="selectContract"
            />
            <AdminSearchSelect
              input-id="location-select"
              label="Standort"
              :options="selectedContract ? selectedContract.locations : []"
              :model-value="selectedLocation"
              :disabled="!selectedContract"
              placeholder="Standort suchen"
              status-mode="features"
              @update:model-value="selectLocation"
            />
            <AdminSearchSelect
              input-id="place-select"
              label="Einsatzstelle"
              :options="selectedLocation ? selectedLocation.places : []"
              :model-value="selectedPlace"
              :disabled="!selectedLocation"
              placeholder="Einsatzstelle suchen"
              status-mode="routes"
              @update:model-value="selectPlace"
            />
          </div>
        </div>
      </div>
      <div v-if="!selectedLocation" class="admin-panel empty-state">
        <i
          class="fa fa-map-location-dot empty-state__icon"
          aria-hidden="true"
        ></i>
        <h2 class="empty-state__title">Kein Standort ausgewählt</h2>
        <p class="empty-state__text">
          Wählen Sie einen Vertrag und anschließend einen Standort aus, um
          Dokumente, Zugriffsrechte und Rundgänge zu verwalten.
        </p>
      </div>
      <template v-else>
        <div class="admin-panel workspace">
          <div class="admin-panel__body">
            <div class="breadcrumb">
              <span class="breadcrumb__item">
                {{ selectedContract.name }}
                <span class="breadcrumb__code">{{ selectedContract.code }}</span>
              </span>
              <i
                class="fa fa-chevron-right breadcrumb__separator"
                aria-hidden="true"
              ></i>
              <span class="breadcrumb__item">
                {{ selectedLocation.name }}
                <span class="breadcrumb__code">{{ selectedLocation.code }}</span>
              </span>
              <template v-if="selectedPlace">
                <i
                  class="fa fa-chevron-right breadcrumb__separator"
                  aria-hidden="true"
                ></i>
                <span class="breadcrumb__item">
                  {{ selectedPlace.name }}
                  <span class="breadcrumb__code">{{ selectedPlace.code }}</span>
                </span>
              </template>
            </div>
            <div class="status-cards">
              <div v-if="canUseDocuments" class="status-card">
                <span class="status-card__label">Dokumente</span>
                <span
                  class="status-pill"
                  :class="{ 'status-pill--success': documentsStatus }"
                >
                  {{ documentsStatus ? "Aktiviert" : "Deaktiviert" }}
                </span>
              </div>
              <div class="status-card">
                <span class="status-card__label">Rundgänge</span>
                <span
                  class="status-pill"
                  :class="{
                    'status-pill--success': selectedLocation.routes
                  }"
                >
                  {{
                    selectedLocation.routes ? "Konfiguriert" : "Keine Rundgänge"
                  }}
                </span>
              </div>
              <div v-if="canManageExternalAccess" class="status-card">
                <span class="status-card__label">Externe Zugriffe</span>
                <span class="status-card__count">
                  {{ locationInfo.permissions.length }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="admin-panel workspace">
          <div class="workspace__tabs">
            <div class="admin-tabs">
              <button
                v-for="tab in availableTabs"
                :key="tab.id"
                type="button"
                class="admin-tab"
                :class="{ 'admin-tab--active': selectedTab === tab.id }"
                @click="setTab(tab.id)"
              >
                <i class="fa" :class="tab.icon" aria-hidden="true"></i>
                {{ tab.label }}
              </button>
            </div>
          </div>
          <div class="admin-panel__body">
            <div v-if="!statsLoaded" class="tab-hint">
              Berechtigungen werden geladen …
            </div>
            <div
              v-else-if="availableTabs.length === 0"
              class="tab-empty"
            >
              <p>Für diesen Bereich sind keine Funktionen freigeschaltet.</p>
            </div>
            <div v-else-if="selectedTab === 'documents'" class="tab-content">
              <div class="docs-toolbar">
                <div>
                  <h3 class="tab-content__title">Dokumentenfunktion</h3>
                  <p class="tab-hint">
                    Ermöglicht Mitarbeitern, Dokumente für diesen Standort zu
                    erstellen.
                  </p>
                </div>
                <div class="docs-toolbar__controls">
                  <label class="date-label" for="doc-date">Tag</label>
                  <input
                    id="doc-date"
                    v-model="selectedDate"
                    type="date"
                    class="date-input"
                    @change="getLocationInfo()"
                  />
                  <button
                    v-if="locationInfo.documentsEnabled"
                    type="button"
                    class="admin-button--danger admin-button--sm"
                    :disabled="actionLoading"
                    @click="setDocuments(false)"
                  >
                    Deaktivieren
                  </button>
                  <button
                    v-else
                    type="button"
                    class="admin-button--primary admin-button--sm"
                    :disabled="actionLoading"
                    @click="setDocuments(true)"
                  >
                    Aktivieren
                  </button>
                </div>
              </div>
              <div v-if="locationLoading" class="tab-hint">
                Dokumente werden geladen …
              </div>
              <template v-else-if="locationInfo.documentsEnabled">
                <div class="table-scroll">
                  <table class="settings-table">
                    <thead>
                      <tr>
                        <th>Erstellt</th>
                        <th>Dokument</th>
                        <th>Verfasser</th>
                        <th class="text-center">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="document in locationInfo.docs"
                        :key="document.id"
                      >
                        <td>
                          {{
                            document.createdAt
                              ? new Date(
                                  document.createdAt
                                ).toLocaleDateString()
                              : "–"
                          }}
                        </td>
                        <td>{{ document.type }}</td>
                        <td>
                          {{
                            document.document &&
                            document.document.author &&
                            document.document.author.name
                              ? document.document.author.name
                              : "–"
                          }}
                        </td>
                        <td class="text-center">
                          <button
                            type="button"
                            class="admin-button--secondary admin-button--sm"
                            :disabled="actionLoading"
                            @click="viewDocument(document)"
                          >
                            Ansehen
                          </button>
                          <button
                            type="button"
                            class="admin-button--danger admin-button--sm"
                            :disabled="actionLoading"
                            @click="deleteDocument(document)"
                          >
                            Löschen
                          </button>
                        </td>
                      </tr>
                      <tr v-if="locationInfo.docs.length === 0">
                        <td colspan="4" class="settings-table__empty">
                          Keine Dokumente für diesen Tag vorhanden.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
              <div v-else class="tab-empty">
                <i
                  class="fa fa-file-lines tab-empty__icon"
                  aria-hidden="true"
                ></i>
                <p>
                  Die Dokumentenfunktion ist für diesen Standort deaktiviert.
                  Mitarbeiter können keine Dokumente für Einsätze an diesem
                  Standort erstellen.
                </p>
                <button
                  type="button"
                  class="admin-button--primary"
                  :disabled="actionLoading"
                  @click="setDocuments(true)"
                >
                  Dokumente aktivieren
                </button>
              </div>
            </div>
            <div v-else-if="selectedTab === 'access'" class="tab-content">
              <h3 class="tab-content__title">Externe Zugriffsrechte</h3>
              <p class="tab-hint">
                Die Freigabe gilt exakt für Vertrag {{ selectedContract.code }}
                und Standort {{ selectedLocation.code }}.
              </p>
              <div v-if="locationLoading" class="tab-hint">
                Freigaben werden geladen …
              </div>
              <template v-else>
                <div class="permission-list">
                  <div
                    v-for="permission in locationInfo.permissions"
                    :key="permission.id"
                    class="permission-row"
                  >
                    <div class="permission-row__user">
                      <strong>{{
                        permission.user ? permission.user.name : "–"
                      }}</strong>
                      <span class="permission-row__mail">{{
                        permission.user ? permission.user.mail : ""
                      }}</span>
                    </div>
                    <span class="permission-row__creator">
                      Erteilt von {{ permission.createdBy || "Unbekannt" }}
                    </span>
                    <input
                      v-model="permission.comment"
                      class="permission-row__comment"
                      type="text"
                      placeholder="Kommentar"
                      aria-label="Kommentar zur Freigabe"
                    />
                    <div class="permission-row__actions">
                      <button
                        type="button"
                        class="admin-button--primary admin-button--sm"
                        :disabled="actionLoading"
                        @click="savePermission(permission)"
                      >
                        Speichern
                      </button>
                      <button
                        type="button"
                        class="admin-button--danger admin-button--sm"
                        :disabled="actionLoading"
                        @click="deleteExternalDocumentPermission(permission)"
                      >
                        Entziehen
                      </button>
                    </div>
                  </div>
                  <p
                    v-if="locationInfo.permissions.length === 0"
                    class="tab-hint"
                  >
                    Für diesen Standort sind keine externen Freigaben
                    eingerichtet.
                  </p>
                </div>
                <div class="permission-new">
                  <h3 class="tab-content__title">Neue Freigabe</h3>
                  <div class="permission-new__grid">
                    <label class="form-field">
                      <span>Name</span>
                      <input
                        v-model="newPermission.user.name"
                        type="text"
                        placeholder="Name des externen Benutzers"
                      />
                    </label>
                    <label class="form-field">
                      <span>E-Mail</span>
                      <input
                        v-model="newPermission.user.mail"
                        type="email"
                        placeholder="name@firma.de"
                      />
                    </label>
                    <label class="form-field form-field--wide">
                      <span>Kommentar</span>
                      <input
                        v-model="newPermission.comment"
                        type="text"
                        placeholder="Optionaler Kommentar"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    class="admin-button--primary"
                    :disabled="actionLoading"
                    @click="savePermission(newPermission, true)"
                  >
                    Freigabe speichern
                  </button>
                </div>
              </template>
            </div>
            <div v-else-if="selectedTab === 'tours'" class="tab-content">
              <h3 class="tab-content__title">Rundgänge</h3>
              <p class="tab-hint">
                Wählen Sie eine Einsatzstelle aus, um deren Rundgänge zu
                verwalten.
              </p>
              <div v-if="!selectedPlace" class="tab-empty">
                <i class="fa fa-route tab-empty__icon" aria-hidden="true"></i>
                <p>
                  Bitte wählen Sie zuerst eine Einsatzstelle aus, um Rundgänge
                  anzuzeigen oder anzulegen.
                </p>
              </div>
              <template v-else>
                <div class="place-header">
                  <div>
                    <h3 class="place-header__title">
                      {{ selectedPlace.name }}
                      <span class="breadcrumb__code">{{
                        selectedPlace.code
                      }}</span>
                    </h3>
                    <div class="place-header__status">
                      <span
                        class="status-pill"
                        :class="{
                          'status-pill--success': selectedPlace.routes
                        }"
                      >
                        {{
                          selectedPlace.routes
                            ? "Rundgang konfiguriert"
                            : "Kein Rundgang"
                        }}
                      </span>
                      <span class="place-header__count">
                        {{ routes.length }} Rundgang/Rundgänge,
                        {{ activeRouteCount }} aktiv
                      </span>
                    </div>
                  </div>
                  <div class="place-header__actions">
                    <button
                      type="button"
                      class="admin-button--primary admin-button--sm"
                      @click="createRoute"
                    >
                      Rundgang erstellen
                    </button>
                    <button
                      type="button"
                      class="admin-button--secondary admin-button--sm"
                      :disabled="jobsLoading"
                      @click="viewJobsWithTours"
                    >
                      {{ jobsLoading ? "Laden …" : "Protokolle anzeigen" }}
                    </button>
                  </div>
                </div>
                <div v-if="routesLoading" class="tab-hint">
                  Rundgänge werden geladen …
                </div>
                <div v-else class="table-scroll">
                  <table class="settings-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Beschreibung</th>
                        <th>Status</th>
                        <th class="text-center">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="route in routes" :key="route.id">
                        <td>{{ route.name }}</td>
                        <td>{{ route.description }}</td>
                        <td>
                          <span
                            class="status-pill"
                            :class="{
                              'status-pill--success': route.active
                            }"
                          >
                            {{ route.active ? "aktiv" : "inaktiv" }}
                          </span>
                        </td>
                        <td class="text-center">
                          <button
                            type="button"
                            class="admin-button--secondary admin-button--sm"
                            @click="viewRoute(route)"
                          >
                            Bearbeiten
                          </button>
                        </td>
                      </tr>
                      <tr v-if="routes.length === 0">
                        <td colspan="4" class="settings-table__empty">
                          Keine Rundgänge für diese Einsatzstelle gefunden.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
      <div
        v-if="jobs !== null"
        ref="jobsDialog"
        class="jobs-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jobs-dialog-title"
        tabindex="-1"
        @keydown.esc="jobs = null"
        @click.self="jobs = null"
      >
        <div class="jobs-dialog__panel">
          <div class="jobs-dialog__header">
            <h2 id="jobs-dialog-title" class="admin-panel__title">
              Protokolle
            </h2>
            <button
              type="button"
              class="admin-button--ghost admin-button--icon"
              aria-label="Protokolle schließen"
              @click="jobs = null"
            >
              <i class="fa fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
          <div class="table-scroll jobs-dialog__table">
            <table class="settings-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Start</th>
                  <th>Ende</th>
                  <th>Mitarbeiter</th>
                  <th>Rundgänge</th>
                  <th class="text-center">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="job in jobs" :key="job.id">
                  <td>{{ job.Datum_von }}</td>
                  <td>{{ job.Soll_Von }}</td>
                  <td>{{ job.Soll_Bis }}</td>
                  <td>{{ job.user }}</td>
                  <td>{{ job.finishedTours }}/{{ job.requiredTours }}</td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="admin-button--secondary admin-button--sm"
                      @click="downloadJob(job)"
                    >
                      PDF
                    </button>
                  </td>
                </tr>
                <tr v-if="jobs.length === 0">
                  <td colspan="6" class="settings-table__empty">
                    Keine Rundgänge gefunden.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from "@/CASP.js";
import { isNavigationFailure, NavigationFailureType } from "vue-router";
import AdminSearchSelect from "@/components/admin/AdminSearchSelect.vue";

export default {
  name: "AdminJobSettings",
  components: { AdminSearchSelect },
  data() {
    return {
      contracts: [],
      routes: [],
      jobs: null,
      openDocuments: [],
      openDocumentsLoading: true,
      stats: {},
      selectedContract: null,
      selectedLocation: null,
      selectedPlace: null,
      selectedDate: new Date().toISOString().substr(0, 10),
      selectedTab: "documents",
      locationInfo: { docs: [], permissions: [], documentsEnabled: false },
      newPermission: { user: { id: -1, name: "", mail: "" }, comment: "" },
      presetsLoading: true,
      locationLoading: false,
      routesLoading: false,
      jobsLoading: false,
      actionLoading: false,
      errorMessage: "",
      successMessage: "",
      locationInfoLoaded: false,
      statsLoaded: false
    };
  },
  computed: {
    permissions() {
      return this.stats.permissions || {};
    },
    canUseDocuments() {
      return Boolean(
        this.permissions.documents ||
          this.permissions.internalDispo ||
          this.permissions.admin
      );
    },
    canUseTours() {
      return Boolean(this.permissions.tours);
    },
    canManageExternalAccess() {
      return Boolean(
        this.permissions.internalDispo || this.permissions.admin
      );
    },
    availableTabs() {
      return [
        this.canUseDocuments && {
          id: "documents",
          label: "Dokumente",
          icon: "fa-file-lines"
        },
        this.canUseTours && {
          id: "tours",
          label: "Rundgänge",
          icon: "fa-route"
        },
        this.canManageExternalAccess && {
          id: "access",
          label: "Zugriffsrechte",
          icon: "fa-user-lock"
        }
      ].filter(Boolean);
    },
    documentsStatus() {
      if (this.locationInfoLoaded) {
        return this.locationInfo.documentsEnabled;
      }
      return Boolean(this.selectedLocation && this.selectedLocation.documents);
    },
    activeRouteCount() {
      return this.routes.filter((route) => route.active).length;
    }
  },
  methods: {
    emptyLocationInfo() {
      return { docs: [], permissions: [], documentsEnabled: false };
    },
    isCurrentLocation(contractCode, locationCode) {
      return Boolean(
        this.selectedContract &&
          this.selectedLocation &&
          this.selectedContract.code === contractCode &&
          this.selectedLocation.code === locationCode
      );
    },
    reset() {
      this.selectedContract = null;
      this.selectedLocation = null;
      this.selectedPlace = null;
      this.routes = [];
      this.jobs = null;
      this.locationLoading = false;
      this.routesLoading = false;
      this.jobsLoading = false;
      this.locationInfo = this.emptyLocationInfo();
      this.locationInfoLoaded = false;
      if (window.localStorage) {
        localStorage.removeItem("selectedContract");
        localStorage.removeItem("selectedLocation");
        localStorage.removeItem("selectedPlace");
      }
      this.syncRouteQuery();
    },
    selectContract(contract, updateRoute = true) {
      this.selectedContract = contract || null;
      this.selectedLocation = null;
      this.selectedPlace = null;
      this.routes = [];
      this.locationLoading = false;
      this.routesLoading = false;
      this.jobsLoading = false;
      this.locationInfo = this.emptyLocationInfo();
      this.locationInfoLoaded = false;
      if (window.localStorage) {
        if (contract) {
          localStorage.setItem("selectedContract", contract.code);
        } else {
          localStorage.removeItem("selectedContract");
        }
        localStorage.removeItem("selectedLocation");
        localStorage.removeItem("selectedPlace");
      }
      if (updateRoute) this.syncRouteQuery();
    },
    selectLocation(location, updateRoute = true) {
      this.selectedLocation = location || null;
      this.selectedPlace = null;
      this.routes = [];
      this.jobs = null;
      this.locationLoading = false;
      this.routesLoading = false;
      this.jobsLoading = false;
      this.locationInfo = this.emptyLocationInfo();
      this.locationInfoLoaded = false;
      if (window.localStorage) {
        if (location) {
          localStorage.setItem("selectedLocation", location.code);
        } else {
          localStorage.removeItem("selectedLocation");
        }
        localStorage.removeItem("selectedPlace");
      }
      if (
        location &&
        (this.canUseDocuments || this.canManageExternalAccess)
      ) {
        this.getLocationInfo();
      }
      if (updateRoute) this.syncRouteQuery();
    },
    selectPlace(place, updateRoute = true) {
      this.selectedPlace = place || null;
      this.routes = [];
      this.routesLoading = false;
      this.jobsLoading = false;
      if (window.localStorage) {
        if (place) {
          localStorage.setItem("selectedPlace", place.code);
        } else {
          localStorage.removeItem("selectedPlace");
        }
      }
      if (place) this.getRoutesForPlace();
      if (updateRoute) this.syncRouteQuery();
    },
    syncRouteQuery() {
      if (this.$route.name !== "AdminJobSettings") return;
      const query = {};
      if (this.selectedContract) query.contract = this.selectedContract.code;
      if (this.selectedLocation) query.location = this.selectedLocation.code;
      if (this.selectedPlace) query.place = this.selectedPlace.code;
      if (this.selectedTab) query.section = this.selectedTab;
      this.$router
        .replace({ name: "AdminJobSettings", query })
        .catch((failure) => {
          if (
            !isNavigationFailure(failure, NavigationFailureType.duplicated)
          ) {
            throw failure;
          }
        });
    },
    setTab(tab) {
      if (!this.availableTabs.some((entry) => entry.id === tab)) return;
      this.selectedTab = tab;
      if (
        (tab === "documents" || tab === "access") &&
        this.selectedLocation &&
        !this.locationInfoLoaded
      ) {
        this.getLocationInfo();
      }
      this.syncRouteQuery();
    },
    normalizeTab() {
      const requested = this.$route.query.section;
      const ids = this.availableTabs.map((tab) => tab.id);
      if (requested && ids.includes(requested)) {
        this.selectedTab = requested;
      } else if (!ids.includes(this.selectedTab)) {
        this.selectedTab = ids[0] || "documents";
      }
    },
    getStats() {
      CASP.getStats((status, response) => {
        this.statsLoaded = true;
        if (status === 200) {
          this.stats = response;
          this.normalizeTab();
          if (
            this.selectedLocation &&
            (this.canUseDocuments || this.canManageExternalAccess) &&
            !this.locationInfoLoaded
          ) {
            this.getLocationInfo();
          }
          if (this.canManageExternalAccess) {
            this.getOpenDocuments();
          }
        } else {
          this.errorMessage =
            "Die Berechtigungen konnten nicht geladen werden.";
        }
      });
    },
    getContracts() {
      this.presetsLoading = true;
      CASP.getPresets("", (presets) => {
        this.contracts =
          presets && Array.isArray(presets.contracts)
            ? presets.contracts.filter((contract) => contract.code)
            : [];
        this.presetsLoading = false;
        if (!presets) {
          this.errorMessage = "Die Standorte konnten nicht geladen werden.";
          return;
        }
        this.restoreSelection();
      });
    },
    restoreSelection() {
      const query = this.$route.query;
      const contractCode =
        query.contract ||
        (window.localStorage ? localStorage.getItem("selectedContract") : null);
      const locationCode =
        query.location ||
        (window.localStorage ? localStorage.getItem("selectedLocation") : null);
      const placeCode =
        query.place ||
        (window.localStorage ? localStorage.getItem("selectedPlace") : null);
      if (contractCode) {
        const contract = this.contracts.find(
          (entry) => entry.code === contractCode
        );
        if (contract) {
          this.selectContract(contract, false);
          if (locationCode) {
            const location = (contract.locations || []).find(
              (entry) => entry.code === locationCode
            );
            if (location) {
              this.selectLocation(location, false);
              if (placeCode) {
                const place = (location.places || []).find(
                  (entry) => entry.code === placeCode
                );
                if (place) this.selectPlace(place, false);
              }
            }
          }
        }
      }
      this.syncRouteQuery();
    },
    getLocationInfo() {
      if (!this.selectedContract || !this.selectedLocation) return;
      const contractCode = this.selectedContract.code;
      const locationCode = this.selectedLocation.code;
      const selectedDate = this.selectedDate;
      this.locationLoading = true;
      this.errorMessage = "";
      CASP.getLocationInfo(
        contractCode,
        locationCode,
        selectedDate,
        (status, res) => {
          if (
            !this.isCurrentLocation(contractCode, locationCode) ||
            this.selectedDate !== selectedDate
          ) {
            return;
          }
          this.locationLoading = false;
          if (status === 200 && res) {
            this.locationInfo = {
              docs: Array.isArray(res.docs) ? res.docs : [],
              permissions: Array.isArray(res.permissions)
                ? res.permissions
                : [],
              documentsEnabled: res.documentsEnabled === true
            };
            this.locationInfoLoaded = true;
          } else {
            this.locationInfo = this.emptyLocationInfo();
            this.errorMessage =
              "Die Standortinformationen konnten nicht geladen werden.";
          }
        }
      );
    },
    getRoutesForPlace() {
      if (!this.selectedPlace) return;
      const placeCode = this.selectedPlace.code;
      this.routesLoading = true;
      CASP.getRoutesForPlace(placeCode, (status, res) => {
        if (!this.selectedPlace || this.selectedPlace.code !== placeCode) {
          return;
        }
        this.routesLoading = false;
        this.routes = status === 200 && Array.isArray(res) ? res : [];
        this.selectedPlace.routes = this.routes.length > 0;
        if (this.routes.length > 0) {
          if (this.selectedLocation) this.selectedLocation.routes = true;
          if (this.selectedContract) this.selectedContract.routes = true;
        }
      });
    },
    getOpenDocuments() {
      if (!this.canManageExternalAccess) {
        this.openDocumentsLoading = false;
        return;
      }
      this.openDocumentsLoading = true;
      CASP.getOpenDocuments((status, res) => {
        this.openDocumentsLoading = false;
        if (status === 200 && Array.isArray(res)) {
          this.openDocuments = res.filter(
            (document) => document.jobdata !== null
          );
        } else {
          this.openDocuments = [];
        }
      });
    },
    setDocuments(value) {
      if (!this.selectedContract || !this.selectedLocation) return;
      const contract = this.selectedContract;
      const location = this.selectedLocation;
      const contractCode = contract.code;
      const locationCode = location.code;
      this.actionLoading = true;
      this.errorMessage = "";
      this.successMessage = "";
      CASP.updateLocation(
        contractCode,
        locationCode,
        { documentsEnabled: value },
        (status) => {
          this.actionLoading = false;
          if (status === 200) {
            location.documents = value === true;
            if (value === true) {
              contract.documents = true;
            } else if (Array.isArray(contract.locations)) {
              contract.documents = contract.locations.some(
                (entry) => entry.documents
              );
            }
            this.successMessage = value
              ? "Dokumente wurden für diesen Standort aktiviert."
              : "Dokumente wurden für diesen Standort deaktiviert.";
            if (this.isCurrentLocation(contractCode, locationCode)) {
              this.getLocationInfo();
            }
          } else {
            this.errorMessage =
              "Die Dokumentenfunktion konnte nicht geändert werden.";
          }
        }
      );
    },
    savePermission(permission, isNew = false) {
      if (!this.selectedContract || !this.selectedLocation) {
        this.errorMessage =
          "Bitte wählen Sie zuerst Vertrag und Standort aus.";
        return;
      }
      const contractCode = this.selectedContract.code;
      const locationCode = this.selectedLocation.code;
      if (!permission || !permission.user) {
        this.errorMessage =
          "Die Freigabe enthält keine gültigen Benutzerdaten.";
        return;
      }
      const name = (permission.user.name || "").trim();
      const mail = (permission.user.mail || "").trim();
      const comment = (permission.comment || "").trim();
      if (isNew && (!name || !mail)) {
        this.errorMessage =
          "Bitte geben Sie Name und E-Mail-Adresse für die neue Freigabe an.";
        return;
      }
      const payload = {
        user: { ...permission.user, name: name, mail: mail },
        comment: comment,
        contract: this.selectedContract,
        location: this.selectedLocation
      };
      this.actionLoading = true;
      this.errorMessage = "";
      this.successMessage = "";
      CASP.updateDocumentUser(payload, (status) => {
        this.actionLoading = false;
        if (status === 200) {
          if (isNew) {
            this.newPermission = {
              user: { id: -1, name: "", mail: "" },
              comment: ""
            };
          }
          this.successMessage = "Die Freigabe wurde gespeichert.";
          if (this.isCurrentLocation(contractCode, locationCode)) {
            this.getLocationInfo();
          }
        } else {
          this.errorMessage =
            "Die Freigabe konnte nicht gespeichert werden.";
        }
      });
    },
    deleteExternalDocumentPermission(permission) {
      if (!confirm("Soll die Freigabe wirklich entzogen werden?")) return;
      const contractCode = this.selectedContract
        ? this.selectedContract.code
        : null;
      const locationCode = this.selectedLocation
        ? this.selectedLocation.code
        : null;
      this.actionLoading = true;
      this.errorMessage = "";
      this.successMessage = "";
      CASP.deleteExternalDocumentPermission(permission, (status) => {
        this.actionLoading = false;
        if (status === 200) {
          this.successMessage = "Die Freigabe wurde entzogen.";
          if (this.isCurrentLocation(contractCode, locationCode)) {
            this.getLocationInfo();
          }
        } else {
          this.errorMessage = "Die Freigabe konnte nicht entzogen werden.";
        }
      });
    },
    deleteDocument(document) {
      if (!confirm("Soll das Dokument wirklich gelöscht werden?")) return;
      const contractCode = this.selectedContract
        ? this.selectedContract.code
        : null;
      const locationCode = this.selectedLocation
        ? this.selectedLocation.code
        : null;
      this.actionLoading = true;
      this.errorMessage = "";
      this.successMessage = "";
      CASP.deleteJobDocument(document.id, (status) => {
        this.actionLoading = false;
        if (status === 200) {
          this.successMessage = "Das Dokument wurde gelöscht.";
          if (this.isCurrentLocation(contractCode, locationCode)) {
            this.getLocationInfo();
          }
          if (this.canManageExternalAccess) this.getOpenDocuments();
        } else {
          this.errorMessage = "Das Dokument konnte nicht gelöscht werden.";
        }
      });
    },
    viewDocument(document) {
      const href = this.$router.resolve({
        name: "ViewJobDocument",
        params: { jobid: document.job, doc: document.id }
      }).href;
      window.open(href, "_blank");
    },
    contextQuery() {
      const query = { section: "tours" };
      if (this.selectedContract) query.contract = this.selectedContract.code;
      if (this.selectedLocation) query.location = this.selectedLocation.code;
      if (this.selectedPlace) query.place = this.selectedPlace.code;
      return query;
    },
    createRoute() {
      this.$router.push({ name: "CreateRoute", query: this.contextQuery() });
    },
    viewRoute(route) {
      this.$router.push({
        name: "ModifyRoute",
        params: { id: route.id },
        query: this.contextQuery()
      });
    },
    viewJobsWithTours() {
      if (!this.selectedPlace) return;
      const placeCode = this.selectedPlace.code;
      this.jobsLoading = true;
      this.errorMessage = "";
      CASP.getJobsWithTours(placeCode, (status, response) => {
        if (!this.selectedPlace || this.selectedPlace.code !== placeCode) {
          return;
        }
        this.jobsLoading = false;
        if (status === 200 && Array.isArray(response)) {
          this.jobs = response;
          this.$nextTick(() => {
            if (this.$refs.jobsDialog) this.$refs.jobsDialog.focus();
          });
        } else {
          this.errorMessage =
            "Die Protokolle konnten nicht geladen werden.";
        }
      });
    },
    downloadJob(job) {
      this.errorMessage = "";
      CASP.getAdminOTL((status, res) => {
        if (status === 200 && res && res.otl) {
          window.open(
            process.env.VUE_APP_API_URL +
              "/routes/jobs/" +
              job.id +
              "/download?otl=" +
              res.otl +
              "&assets",
            "_blank"
          );
        } else {
          this.errorMessage =
            "Der Download-Link konnte nicht erstellt werden.";
        }
      });
    }
  },
  created() {
    this.getStats();
    this.getContracts();
    const spinner = document.getElementById("mainspinner");
    if (spinner) spinner.style.display = "none";
  }
};
</script>

<style scoped>
.settings-message {
  margin-bottom: 1rem;
  border: 1px solid var(--admin-line);
  border-radius: 0.65rem;
  padding: 0.7rem 1rem;
  font-size: 0.92rem;
  font-weight: 650;
}

.settings-message--error {
  border-color: #d9a6a6;
  background: #fbeeee;
  color: var(--admin-danger);
}

.settings-message--success {
  border-color: #9cc79c;
  background: #e5f3e5;
  color: #2f6b2f;
}

.open-docs {
  margin-bottom: 1.25rem;
}

.open-docs__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.open-docs__count {
  min-width: 1.7rem;
  border-radius: 999px;
  background: var(--admin-yellow);
  color: var(--admin-ink);
  padding: 0.1rem 0.5rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 800;
}

.open-docs__body {
  padding: 0;
}

.open-docs__loading {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
}

.open-docs__spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 3px solid var(--admin-line);
  border-top-color: var(--admin-ink);
  border-radius: 50%;
  animation: open-docs-spin 700ms linear infinite;
}

@keyframes open-docs-spin {
  to {
    transform: rotate(360deg);
  }
}

.open-docs .table-scroll {
  max-height: min(24rem, 50vh);
  overflow: auto;
}

.open-docs .settings-table th {
  position: sticky;
  z-index: 1;
  top: 0;
  background: var(--admin-surface);
}

.selection-panel {
  margin-bottom: 1.25rem;
}

.selection-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.selection-panel__hint {
  margin: 0.2rem 0 0;
  color: var(--admin-muted);
  font-size: 0.85rem;
}

.selection-panel__loading {
  color: var(--admin-muted);
  padding: 0.5rem 0;
}

.selection-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
}

.settings-table {
  width: 100%;
  min-width: 34rem;
  border-collapse: collapse;
}

.settings-table th {
  border-bottom: 1px solid var(--admin-line);
  color: var(--admin-muted);
  padding: 0.6rem 0.75rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.settings-table td {
  border-bottom: 1px solid var(--admin-line);
  padding: 0.6rem 0.75rem;
  font-size: 0.92rem;
  vertical-align: middle;
}

.settings-table tr:last-child td {
  border-bottom: 0;
}

.settings-table .text-center,
.settings-table__empty {
  text-align: center;
}

.settings-table td .admin-button--sm + .admin-button--sm {
  margin-left: 0.35rem;
}

.settings-table__empty {
  color: var(--admin-muted);
  padding: 1.25rem 0.75rem;
}

.empty-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 3rem 1.5rem;
  text-align: center;
}

.empty-state__icon {
  color: var(--admin-line-strong);
  font-size: 2rem;
}

.empty-state__title {
  margin: 0.75rem 0 0.35rem;
  font-size: 1.15rem;
  font-weight: 800;
}

.empty-state__text {
  max-width: 32rem;
  margin: 0;
  color: var(--admin-muted);
}

.workspace {
  margin-bottom: 1.25rem;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.breadcrumb__code {
  margin-left: 0.3rem;
  border-radius: 0.4rem;
  background: var(--admin-background);
  color: var(--admin-muted);
  padding: 0.1rem 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.breadcrumb__separator {
  color: var(--admin-line-strong);
  font-size: 0.65rem;
}

.status-cards {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
}

.status-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  border: 1px solid var(--admin-line);
  border-radius: 0.65rem;
  background: var(--admin-background);
  padding: 0.7rem 0.9rem;
}

.status-card__label {
  color: var(--admin-muted);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-card__count {
  font-size: 1.15rem;
  font-weight: 800;
}

.status-pill {
  display: inline-block;
  border: 1px solid var(--admin-line);
  border-radius: 999px;
  background: var(--admin-surface);
  color: var(--admin-muted);
  padding: 0.15rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill--success {
  border-color: #9cc79c;
  background: #e5f3e5;
  color: #2f6b2f;
}

.workspace__tabs {
  padding: 0.5rem 1.25rem 0;
}

.workspace__tabs .admin-tabs {
  margin-bottom: 0;
}

.tab-content__title {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  font-weight: 800;
}

.tab-hint {
  margin: 0.25rem 0 1rem;
  color: var(--admin-muted);
  font-size: 0.9rem;
}

.docs-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.docs-toolbar__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.date-label {
  color: var(--admin-muted);
  font-size: 0.85rem;
  font-weight: 700;
}

.date-input {
  min-height: 2.2rem;
  border: 1px solid var(--admin-line-strong);
  border-radius: 0.5rem;
  background: var(--admin-surface);
  color: var(--admin-ink);
  padding: 0.3rem 0.6rem;
}

.tab-empty {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px dashed var(--admin-line-strong);
  border-radius: 0.65rem;
  padding: 2rem 1.25rem;
  text-align: center;
  color: var(--admin-muted);
}

.tab-empty__icon {
  font-size: 1.6rem;
}

.tab-empty p {
  max-width: 30rem;
  margin: 0;
}

.permission-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}

.permission-row {
  display: grid;
  align-items: center;
  gap: 0.6rem;
  border: 1px solid var(--admin-line);
  border-radius: 0.65rem;
  padding: 0.7rem 0.9rem;
  grid-template-columns: minmax(10rem, 1.2fr) minmax(9rem, 1fr) minmax(10rem, 1fr) auto;
}

.permission-row__user {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.permission-row__mail {
  overflow: hidden;
  color: var(--admin-muted);
  font-size: 0.85rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.permission-row__creator {
  color: var(--admin-muted);
  font-size: 0.82rem;
}

.permission-row__comment {
  min-height: 2.2rem;
  min-width: 0;
  border: 1px solid var(--admin-line-strong);
  border-radius: 0.5rem;
  padding: 0.3rem 0.6rem;
}

.permission-row__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
}

.permission-new {
  border-top: 1px solid var(--admin-line);
  padding-top: 1.25rem;
}

.permission-new__grid {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.3rem;
}

.form-field span {
  color: var(--admin-muted);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.form-field input {
  min-height: 2.4rem;
  border: 1px solid var(--admin-line-strong);
  border-radius: 0.5rem;
  background: var(--admin-surface);
  color: var(--admin-ink);
  padding: 0.4rem 0.65rem;
}

.place-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.place-header__title {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 800;
}

.place-header__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
}

.place-header__count {
  color: var(--admin-muted);
  font-size: 0.85rem;
}

.place-header__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.jobs-dialog {
  position: fixed;
  z-index: 60;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  background: rgba(12, 15, 20, 0.58);
  padding: 3rem 1rem;
}

.jobs-dialog:focus {
  outline: none;
}

.jobs-dialog__panel {
  width: 100%;
  max-width: 60rem;
  border: 1px solid var(--admin-line);
  border-radius: 0.85rem;
  background: var(--admin-surface);
  box-shadow: 0 24px 60px rgba(12, 15, 20, 0.35);
}

.jobs-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--admin-line);
  padding: 0.85rem 1.25rem;
}

.jobs-dialog__table {
  max-height: 65vh;
  overflow-y: auto;
}

@media (max-width: 900px) {
  .selection-grid,
  .permission-new__grid {
    grid-template-columns: 1fr;
  }

  .permission-row {
    grid-template-columns: 1fr;
  }

  .permission-row__actions {
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .docs-toolbar {
    flex-direction: column;
  }

  .place-header {
    flex-direction: column;
  }

  .jobs-dialog {
    padding: 1rem 0.5rem;
  }
}
</style>
